import graphqlClient from '../../common/api/graphql';
import {
  GetOrganizationUsers,
  GetOrganizationUsersForBot,
  GetUserAssignedBotSessions,
  GetUserAssignmentsCount,
  GetUsersOrganizationSalesUsers,
} from '../../common/queries/queries';
import { getAll, getData, orgPath } from './restApi';

// Ola 2: 'true' reads memberships, org members, the user's bots and assignments from main_service REST.
export const isOrgMembersRest = (): boolean => process.env.NEXT_PUBLIC_REST_ORG_MEMBERS === 'true';

// Untyped like the Hasura rows the screens already handle.
type Row = any;

// GetUserOrganizationsLight rows (users_organizations of the caller with its organization), from GET /me.
export async function fetchMyMembershipsRest(): Promise<Row[]> {
  const me = await getData('me');
  // A membership without its organization is skipped, as the store drops those Hasura rows.
  return (me.memberships || []).filter((m: Row) => m?.organization?.id)
    .map(({ organization: { members_count: count, ...o }, role, ...m }: Row) => ({
      ...m,
      rol: role,
      organization: { ...o, users_organizations_aggregate: { aggregate: { count } } },
    }));
}

// Live bots the caller reaches in these orgs; omitting the filter avoids a 403 for an inactive membership.
export async function fetchMyBotsRest(orgIds: string[]): Promise<Row[]> {
  const wanted = new Set(orgIds);
  return (await getAll('me/bots')).filter((b) => wanted.has(b.organization_id));
}

export type OrgMembersFilter = { botId?: string; salesOnly?: boolean };

export const toHasuraMember = ({
  user_id: id, role, name, email, is_available: available, ...m
}: Row) => ({
  ...m,
  user_id: id,
  rol: role,
  user: {
    id, name, email, is_available: available,
  },
});

// users_organizations rows, Hasura shape: active members (GetOrganizationUsers), of a bot plus
// org-wide (GetOrganizationUsersForBot), or sales active or not (GetUsersOrganizationSalesUsers).
export async function loadOrgMembers(orgId: string, filter: OrgMembersFilter = {}): Promise<Row[]> {
  const { botId, salesOnly } = filter;
  if (isOrgMembersRest()) {
    const params = new URLSearchParams(salesOnly ? [['role', 'sales']] : [['active', 'true']]);
    if (botId) params.set('bot_id', botId);
    return (await getAll(orgPath(orgId, '/members'), params)).map(toHasuraMember);
  }
  let query = GetOrganizationUsers;
  if (salesOnly) query = GetUsersOrganizationSalesUsers;
  else if (botId) query = GetOrganizationUsersForBot;
  const { data } = await graphqlClient.query({
    query,
    variables: { organization_id: orgId, ...(botId ? { bot_id: botId } : {}) },
    fetchPolicy: 'no-cache',
  });
  return data?.users_organizations || [];
}

const assignmentsPath = (orgId: string, userId: string) => (
  orgPath(orgId, `/members/${encodeURIComponent(userId)}/assignments`)
);

export async function countMemberAssignments(orgId: string, userId: string): Promise<number> {
  if (isOrgMembersRest()) {
    const page = await getData(assignmentsPath(orgId, userId), new URLSearchParams({ count_only: 'true' }));
    return page.total ?? 0;
  }
  const { data } = await graphqlClient.query({
    query: GetUserAssignmentsCount,
    variables: { user_id: userId, organization_id: orgId },
    fetchPolicy: 'no-cache',
  });
  return data?.bot_sessions_aggregate?.aggregate?.count || 0;
}

// Live sessions of the org assigned to the user, with their contact.
export async function loadMemberAssignments(orgId: string, userId: string): Promise<Row[]> {
  if (isOrgMembersRest()) return getAll(assignmentsPath(orgId, userId));
  const { data } = await graphqlClient.query({
    query: GetUserAssignedBotSessions,
    variables: { sales_assigned_id: userId, organization_id: orgId },
    fetchPolicy: 'no-cache',
  });
  return data?.bot_sessions || [];
}
